<template>
  <NotificationMessenger ref="notification" :message="notificationMessage" />

  <section>
    <div class="titulo">
      <div class="margem container">
        <div class="linha" style="justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 0.5rem">
          <h2>Registro de Inconformidades</h2>
          <div class="linha" style="gap: 0.5rem; align-items: center">
            <button v-if="temPermissao(202)" class="acao-secundaria" @click="abrirModalCategoriasMotivos">Categorias e motivos</button>
            <input type="text" placeholder="Filtrar por OP" v-model="filtroOp" @keyup.enter="aplicarFiltro"
              style="max-width: 180px" />
            <button class="acao-secundaria" @click="aplicarFiltro">Filtrar</button>
          </div>
        </div>
      </div>
    </div>

    <div class="margem container">
      <div v-if="isLoading" class="loading">
        <div></div>
      </div>
      <div v-if="!isLoading" class="margem bloco container">
        <table class="tabela">
          <tbody>
            <tr>
              <th>Datahora</th>
              <th>OP</th>
              <th>Produto</th>
              <th>Quantidade</th>
              <th>Motivo</th>
              <th>Categoria</th>
              <th>Registrado por</th>
              <th v-if="temPermissao(201)">Ações</th>
            </tr>
            <tr v-if="pecas.length === 0">
              <td colspan="8" style="text-align: center; padding: 12px">Nenhum registro encontrado.</td>
            </tr>
            <tr v-for="peca in pecas" :key="peca.id">
              <td>{{ formatDataHora(peca.created_at) }}</td>
              <td>{{ peca.producao?.opNum || peca.op_cod }}</td>
              <td>{{ peca.produto ? `${peca.produto.cod || peca.produto_cod} - ${peca.produto.desc || ""}` :
                peca.produto_cod }}</td>
              <td>{{ peca.quantidade }}</td>
              <td>{{ motivoPeca(peca)?.descricao || "-" }}</td>
              <td>{{ motivoPeca(peca)?.categoria?.descricao || "-" }}</td>
              <td>{{ peca.pessoa?.nomeCompleto || "-" }}</td>
              <td v-if="temPermissao(201)" class="alinha-centro">
                <button class="acao-secundaria chip" @click="abrirEditar(peca)">Editar</button>
                <button class="chip bg-erro" @click="abrirExcluir(peca)">Excluir</button>
              </td>
            </tr>
          </tbody>
        </table>

        <PaginationComponent v-if="pecas.length > 0" :current-page="paginationData.currentPage"
          :total-pages="paginationData.lastPage" :per-page="paginationData.perPage" :total="paginationData.total"
          :from="paginationData.from" :to="paginationData.to" @page-changed="handlePageChange"
          @per-page-changed="handlePerPageChange" />
      </div>
    </div>
  </section>

  <!-- Modal Editar -->
  <SimpleModal :show="modalEditar" @close="fecharEditar">
    <div class="coluna" style="gap: 1rem; min-width: 320px">
      <h3>Editar registro</h3>
      <div v-if="pecaEmEdicao">
        <div class="coluna margem">
          <label>OP</label>
          <input type="text" :value="pecaEmEdicao.producao?.opNum || pecaEmEdicao.op_cod" disabled
            class="input-padrao" />
        </div>
        <div class="coluna margem">
          <label>Produto</label>
          <input type="text"
            :value="pecaEmEdicao.produto ? `${pecaEmEdicao.produto.cod} - ${pecaEmEdicao.produto.desc}` : pecaEmEdicao.produto_cod"
            disabled class="input-padrao" />
        </div>
        <div class="coluna margem">
          <label>Quantidade</label>
          <input type="number" v-model.number="editForm.quantidade" min="1" class="input-padrao" />
        </div>
        <div class="coluna margem">
          <label>Motivo</label>
          <select v-model.number="editForm.inconformidade_motivo_id" class="input-padrao">
            <option v-for="m in motivos" :key="m.id" :value="m.id">{{ m.descricao }}</option>
          </select>
          <p v-if="!motivos.length" class="margem">Nenhum motivo cadastrado.</p>
        </div>
        <div class="coluna margem">
          <label>Registrado por</label>
          <select v-model.number="editForm.pessoa_id" class="input-padrao">
            <option :value="null">Selecione a pessoa</option>
            <option v-for="p in pessoas" :key="p.id" :value="p.id">{{ p.nomeCompleto || p.nome }}</option>
          </select>
          <p v-if="!pessoas.length" class="margem">Nenhuma pessoa carregada.</p>
        </div>
      </div>
      <div class="linha" style="justify-content: flex-end; gap: 0.5rem">
        <button class="acao-secundaria" @click="fecharEditar">Cancelar</button>
        <button :disabled="salvandoEdicao" @click="salvarEdicao">{{ salvandoEdicao ? "Salvando..." : "Salvar"
          }}</button>
      </div>
    </div>
  </SimpleModal>

  <!-- Modal Excluir -->
  <ActionModal :show="modalExcluir" @close="modalExcluir = false">
    <div style="text-align: center; width: 100%">
      <h2>Tem certeza que deseja excluir este registro de inconformidade?</h2>
      <p v-if="pecaEmExclusao">
        OP {{ pecaEmExclusao.producao?.opNum || pecaEmExclusao.op_cod }} - Quantidade: {{ pecaEmExclusao.quantidade }} -
        {{ motivoPeca(pecaEmExclusao)?.descricao }}
      </p>
    </div>
    <template #actions>
      <button class="acao-secundaria" @click="modalExcluir = false">Cancelar</button>
      <button v-if="!excluindo" class="bg-erro" @click="confirmarExcluir">Excluir</button>
      <span v-else>Excluindo...</span>
    </template>
  </ActionModal>

  <!-- Modal Categorias e motivos -->
  <SimpleModal :show="modalCategoriasMotivos" @close="fecharModalCategoriasMotivos" class="modal-categorias-motivos">
    <div class="modal-cat-motivos">
      <h3 class="modal-cat-motivos__titulo">Categorias e motivos</h3>
      <p class="modal-cat-motivos__subtitulo">Gerencie categorias e cadastre motivos de inconformidade.</p>

      <div class="modal-cat-motivos__chips">
        <button type="button" class="modal-cat-motivos__chip"
          :class="{ 'modal-cat-motivos__chip--ativo': modoModalCategorias === 'existente' }"
          @click="modoModalCategorias = 'existente'">
          Categoria existente
        </button>
        <button type="button" class="modal-cat-motivos__chip"
          :class="{ 'modal-cat-motivos__chip--ativo': modoModalCategorias === 'nova' }"
          @click="modoModalCategorias = 'nova'">
          Nova categoria
        </button>
      </div>

      <!-- Modo: categoria existente -->
      <div v-if="modoModalCategorias === 'existente'" class="modal-cat-motivos__painel">
        <div class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Categoria</label>
          <template v-if="editandoCategoriaId != null && editandoCategoriaId === categoriaSelecionadaId">
            <div class="linha" style="gap: 0.5rem; align-items: center; flex-wrap: wrap">
              <input v-model="descricaoCategoriaEditando" type="text" class="modal-cat-motivos__input"
                placeholder="Nome da categoria" style="flex: 1; min-width: 160px"
                @keyup.enter="salvarEdicaoCategoria" />
              <button type="button" class="modal-cat-motivos__btn modal-cat-motivos__btn--primario"
                :disabled="salvandoEdicaoCategoria || !descricaoCategoriaEditando.trim()"
                @click="salvarEdicaoCategoria">
                {{ salvandoEdicaoCategoria ? "..." : "Salvar" }}
              </button>
              <button type="button" class="modal-cat-motivos__btn modal-cat-motivos__btn--secundario"
                :disabled="salvandoEdicaoCategoria" @click="cancelarEditarCategoria">
                Cancelar
              </button>
            </div>
          </template>
          <template v-else>
            <div class="linha" style="gap: 0.5rem; align-items: center; flex-wrap: wrap">
              <select v-model="categoriaSelecionadaId" class="modal-cat-motivos__input"
                style="flex: 1; min-width: 160px">
                <option :value="null">Selecione uma categoria</option>
                <option v-for="c in categorias" :key="c.id" :value="c.id">{{ c.descricao }}</option>
              </select>
              <button v-if="categoriaSelecionadaId" type="button"
                class="modal-cat-motivos__btn modal-cat-motivos__btn--secundario" @click="abrirEditarCategoria">
                Editar
              </button>
              <button v-if="categoriaSelecionadaId" type="button"
                class="modal-cat-motivos__btn modal-cat-motivos__btn--secundario" @click="abrirExcluirCategoria">
                Excluir
              </button>
            </div>
          </template>
        </div>
        <div v-if="categoriaSelecionadaId" class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Setores associados a esta categoria</label>
          <div class="modal-cat-motivos__lista--grid">
            <div v-if="carregandoSetoresModal" class="modal-cat-motivos__vazio">Carregando...</div>
            <template v-else-if="setoresCategoriaModal.length">
              <div v-for="s in setoresCategoriaModal" :key="s.id"
                class="modal-cat-motivos__item modal-cat-motivos__item--com-acao">
                <span class="modal-cat-motivos__item-texto">{{ s.nome }}</span>
                <button type="button" class="modal-cat-motivos__item-btn-remover" title="Remover setor da categoria"
                  :disabled="salvandoSetores" @click="removerSetorDaCategoria(s)">
                  Remover
                </button>
              </div>
            </template>
            <p v-else class="modal-cat-motivos__vazio">Nenhum setor associado.</p>
          </div>
          <div class="linha margem" style="gap: 0.5rem; align-items: center; flex-wrap: wrap">
            <select v-model="setorIdParaAdicionar" class="modal-cat-motivos__input" style="flex: 1; min-width: 160px">
              <option :value="null">Adicionar setor...</option>
              <option v-for="s in setoresDisponiveisParaAdicionar" :key="s.id" :value="s.id">{{ s.nome }}</option>
            </select>
            <button type="button" class="modal-cat-motivos__btn modal-cat-motivos__btn--secundario"
              :disabled="salvandoSetores || !setorIdParaAdicionar" @click="adicionarSetorNaCategoria">
              {{ salvandoSetores ? "..." : "Adicionar" }}
            </button>
          </div>
        </div>
        <div v-if="categoriaSelecionadaId" class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Motivos nesta categoria</label>
          <div class="modal-cat-motivos__lista">
            <div v-if="carregandoMotivosModal" class="modal-cat-motivos__vazio">Carregando...</div>
            <template v-else-if="motivosCategoriaModal.length">
              <div v-for="m in motivosCategoriaModal" :key="m.id"
                class="modal-cat-motivos__item modal-cat-motivos__item--com-acao">
                <span class="modal-cat-motivos__item-texto">{{ m.descricao }}</span>
                <button type="button" class="modal-cat-motivos__item-btn-remover" title="Desassociar e excluir motivo"
                  :disabled="excluindoMotivoId === m.id" @click="abrirExcluirMotivo(m)">
                  {{ excluindoMotivoId === m.id ? "..." : "Remover" }}
                </button>
              </div>
            </template>
            <p v-else class="modal-cat-motivos__vazio">Nenhum motivo associado.</p>
          </div>
        </div>
        <div class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Novo motivo</label>
          <input type="text" v-model="novoMotivoDescricao" placeholder="Digite a descrição do motivo"
            class="modal-cat-motivos__input" />
        </div>
        <div class="modal-cat-motivos__acoes">
          <button type="button" class="modal-cat-motivos__btn modal-cat-motivos__btn--primario"
            :disabled="salvandoMotivo || !categoriaSelecionadaId || !novoMotivoDescricao.trim()"
            @click="criarMotivoNaCategoriaExistente">
            {{ salvandoMotivo ? "Salvando..." : "Criar motivo" }}
          </button>
        </div>
      </div>

      <!-- Modo: criar nova categoria + motivo -->
      <div v-else class="modal-cat-motivos__painel">
        <div class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Nome da categoria</label>
          <input type="text" v-model="novaCategoriaDescricao" placeholder="Ex.: Mecânica"
            class="modal-cat-motivos__input" />
        </div>
        <div class="modal-cat-motivos__campo">
          <label class="modal-cat-motivos__label">Primeiro motivo</label>
          <input type="text" v-model="novoMotivoNovaCategoria" placeholder="Descrição do motivo"
            class="modal-cat-motivos__input" />
        </div>
        <div class="modal-cat-motivos__acoes">
          <button type="button" class="modal-cat-motivos__btn modal-cat-motivos__btn--primario"
            :disabled="salvandoCategoria || !novaCategoriaDescricao.trim() || !novoMotivoNovaCategoria.trim()"
            @click="criarCategoriaEMotivo">
            {{ salvandoCategoria ? "Salvando..." : "Criar categoria e motivo" }}
          </button>
        </div>
      </div>
    </div>
  </SimpleModal>

  <!-- Modal confirmar exclusão de motivo -->
  <ActionModal :show="modalExcluirMotivo" @close="fecharExcluirMotivo">
    <div style="text-align: center; width: 100%">
      <h2>Desassociar e excluir motivo?</h2>
      <p v-if="motivoEmExclusao">
        O motivo <strong>«{{ motivoEmExclusao.descricao }}»</strong> será excluído permanentemente. Todo motivo precisa
        estar associado a uma categoria, portanto a desassociação implica na exclusão.
      </p>
      <p style="margin-top: 0.75rem; font-size: 0.9rem; color: var(--cor-texto-secundario)">
        Registros de peças não conformes que usam este motivo não serão alterados.
      </p>
    </div>
    <template #actions>
      <button class="acao-secundaria" @click="fecharExcluirMotivo">Cancelar</button>
      <button v-if="!excluindoMotivo" class="bg-erro" @click="confirmarExcluirMotivo">Excluir motivo</button>
      <span v-else>Excluindo...</span>
    </template>
  </ActionModal>

  <!-- Modal confirmar exclusão de categoria -->
  <ActionModal :show="modalExcluirCategoria" @close="fecharExcluirCategoria">
    <div style="text-align: center; width: 100%">
      <h2>Excluir categoria?</h2>
      <p v-if="categoriaEmExclusao">
        A categoria <strong>«{{ categoriaEmExclusao.descricao }}»</strong> será excluída permanentemente.
      </p>
      <p style="margin-top: 0.75rem; font-size: 0.9rem; color: var(--cor-texto-secundario)">
        Só é possível excluir categorias que não possuem motivos vinculados. Se houver motivos, exclua-os antes.
      </p>
    </div>
    <template #actions>
      <button class="acao-secundaria" @click="fecharExcluirCategoria">Cancelar</button>
      <button v-if="!excluindoCategoria" class="bg-erro" @click="confirmarExcluirCategoria">Excluir categoria</button>
      <span v-else>Excluindo...</span>
    </template>
  </ActionModal>
</template>

<script>
import { SimpleModal, ActionModal, PaginationComponent, usePermissions } from "roboflex-thalamus-componentes-vue3-lib";
import InconformidadeDataSource from "@/data/datasource/InconformidadeDataSource";
import NotificationMessenger from "@/components/NotificationMessenger.vue";

export default {
  name: "InconformidadesProducao",
  components: {
    SimpleModal,
    ActionModal,
    PaginationComponent,
    NotificationMessenger,
  },
  setup() {
    const { temPermissao } = usePermissions();
    return { temPermissao };
  },
  data() {
    return {
      notificationMessage: "",
      filtroOp: "",
      pecas: [],
      categorias: [],
      motivos: [],
      isLoading: false,
      paginationData: {
        currentPage: 1,
        lastPage: 1,
        perPage: 25,
        total: 0,
        from: 0,
        to: 0,
      },
      modalEditar: false,
      modalExcluir: false,
      pecaEmEdicao: null,
      pecaEmExclusao: null,
      editForm: { quantidade: 1, inconformidade_motivo_id: null, pessoa_id: null },
      pessoas: [],
      salvandoEdicao: false,
      excluindo: false,
      modalCategoriasMotivos: false,
      modoModalCategorias: "existente",
      categoriaSelecionadaId: null,
      novoMotivoDescricao: "",
      novaCategoriaDescricao: "",
      novoMotivoNovaCategoria: "",
      salvandoMotivo: false,
      salvandoCategoria: false,
      motivosCategoriaModal: [],
      carregandoMotivosModal: false,
      setoresCategoriaModal: [],
      setoresTodos: [],
      carregandoSetoresModal: false,
      salvandoSetores: false,
      setorIdParaAdicionar: null,
      modalExcluirMotivo: false,
      motivoEmExclusao: null,
      excluindoMotivo: false,
      excluindoMotivoId: null,
      editandoCategoriaId: null,
      descricaoCategoriaEditando: "",
      salvandoEdicaoCategoria: false,
      modalExcluirCategoria: false,
      categoriaEmExclusao: null,
      excluindoCategoria: false,
    };
  },
  computed: {
    setoresDisponiveisParaAdicionar() {
      const idsAssociados = (this.setoresCategoriaModal || []).map((s) => s.id);
      return (this.setoresTodos || []).filter((s) => !idsAssociados.includes(s.id));
    },
  },
  watch: {
    categoriaSelecionadaId(val) {
      if (!val) {
        this.motivosCategoriaModal = [];
        this.setoresCategoriaModal = [];
        this.setorIdParaAdicionar = null;
        return;
      }
      this.carregarMotivosCategoriaModal();
      this.carregarSetoresCategoriaModal();
    },
  },
  async mounted() {
    await this.carregarCategorias();
    await this.carregarMotivos();
    await this.carregarPecas();
  },
  methods: {
    getFilters() {
      const f = {};
      if (this.filtroOp != null && String(this.filtroOp).trim() !== "") f.op = String(this.filtroOp).trim();
      return f;
    },
    async aplicarFiltro() {
      this.paginationData.currentPage = 1;
      await this.carregarPecas();
    },
    async carregarPecas() {
      this.isLoading = true;
      try {
        const { data, pagination } = await InconformidadeDataSource.getPecas(
          this.paginationData.currentPage,
          this.paginationData.perPage,
          this.getFilters()
        );
        this.pecas = data;
        this.paginationData = { ...this.paginationData, ...pagination };
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar registros.";
      } finally {
        this.isLoading = false;
      }
    },
    async carregarCategorias() {
      try {
        this.categorias = await InconformidadeDataSource.getCategorias();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar categorias.";
      }
    },
    async carregarMotivos() {
      try {
        this.motivos = await InconformidadeDataSource.getMotivos();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar motivos.";
      }
    },
    async carregarMotivosCategoriaModal() {
      if (!this.categoriaSelecionadaId) {
        this.motivosCategoriaModal = [];
        return;
      }
      this.carregandoMotivosModal = true;
      try {
        this.motivosCategoriaModal = await InconformidadeDataSource.getMotivos([this.categoriaSelecionadaId]);
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar motivos.";
        this.motivosCategoriaModal = [];
      } finally {
        this.carregandoMotivosModal = false;
      }
    },
    async carregarSetoresTodos() {
      try {
        this.setoresTodos = await InconformidadeDataSource.getSetoresTodos();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar setores.";
        this.setoresTodos = [];
      }
    },
    async carregarSetoresCategoriaModal() {
      if (!this.categoriaSelecionadaId) {
        this.setoresCategoriaModal = [];
        return;
      }
      this.carregandoSetoresModal = true;
      try {
        this.setoresCategoriaModal = await InconformidadeDataSource.getSetoresCategoria(this.categoriaSelecionadaId);
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar setores da categoria.";
        this.setoresCategoriaModal = [];
      } finally {
        this.carregandoSetoresModal = false;
      }
    },
    async adicionarSetorNaCategoria() {
      if (!this.categoriaSelecionadaId || !this.setorIdParaAdicionar) return;
      this.salvandoSetores = true;
      try {
        const ids = this.setoresCategoriaModal.map((s) => s.id);
        ids.push(this.setorIdParaAdicionar);
        this.setoresCategoriaModal = await InconformidadeDataSource.syncSetoresCategoria(
          this.categoriaSelecionadaId,
          ids
        );
        this.setorIdParaAdicionar = null;
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao adicionar setor.";
      } finally {
        this.salvandoSetores = false;
      }
    },
    async removerSetorDaCategoria(setor) {
      if (!this.categoriaSelecionadaId) return;
      this.salvandoSetores = true;
      try {
        const ids = this.setoresCategoriaModal.filter((s) => s.id !== setor.id).map((s) => s.id);
        this.setoresCategoriaModal = await InconformidadeDataSource.syncSetoresCategoria(
          this.categoriaSelecionadaId,
          ids
        );
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao remover setor.";
      } finally {
        this.salvandoSetores = false;
      }
    },
    async handlePageChange(page) {
      this.paginationData.currentPage = page;
      await this.carregarPecas();
    },
    async handlePerPageChange(perPage) {
      this.paginationData.perPage = perPage;
      this.paginationData.currentPage = 1;
      await this.carregarPecas();
    },
    async abrirEditar(peca) {
      this.pecaEmEdicao = peca;
      const motivo = this.motivoPeca(peca);
      this.editForm = {
        quantidade: peca.quantidade,
        inconformidade_motivo_id: motivo?.id ?? peca.inconformidade_motivo_id,
        pessoa_id: peca.pessoa_id ?? peca.pessoa?.id ?? null,
      };
      if (this.pessoas.length === 0) {
        try {
          this.pessoas = await InconformidadeDataSource.getPessoas();
        } catch (err) {
          this.notificationMessage = err.response?.data?.message || err.message || "Erro ao carregar pessoas.";
        }
      }
      this.modalEditar = true;
    },
    fecharEditar() {
      this.modalEditar = false;
      this.pecaEmEdicao = null;
    },
    async salvarEdicao() {
      if (!this.pecaEmEdicao) return;
      if (this.editForm.pessoa_id == null) {
        this.notificationMessage = "Selecione quem registrou (Registrado por).";
        return;
      }
      this.salvandoEdicao = true;
      try {
        await InconformidadeDataSource.updatePeca(this.pecaEmEdicao.id, {
          op_cod: this.pecaEmEdicao.op_cod,
          produto_cod: this.pecaEmEdicao.produto_cod,
          quantidade: this.editForm.quantidade,
          inconformidade_motivo_id: this.editForm.inconformidade_motivo_id,
          pessoa_id: this.editForm.pessoa_id,
        });
        this.fecharEditar();
        await this.carregarPecas();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao salvar.";
      } finally {
        this.salvandoEdicao = false;
      }
    },
    abrirExcluir(peca) {
      this.pecaEmExclusao = peca;
      this.modalExcluir = true;
    },
    async confirmarExcluir() {
      if (!this.pecaEmExclusao) return;
      this.excluindo = true;
      try {
        await InconformidadeDataSource.deletePeca(this.pecaEmExclusao.id);
        this.modalExcluir = false;
        this.pecaEmExclusao = null;
        await this.carregarPecas();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao excluir.";
      } finally {
        this.excluindo = false;
      }
    },
    motivoPeca(peca) {
      return peca?.inconformidadeMotivo || peca?.inconformidade_motivo || null;
    },
    formatDataHora(createdAt) {
      if (!createdAt) return "-";
      const d = new Date(createdAt);
      if (isNaN(d.getTime())) return "-";
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const hh = String(d.getHours()).padStart(2, "0");
      const min = String(d.getMinutes()).padStart(2, "0");
      return `${dd}/${mm} - ${hh}:${min}`;
    },
    abrirModalCategoriasMotivos() {
      this.modalCategoriasMotivos = true;
      this.modoModalCategorias = "existente";
      this.categoriaSelecionadaId = null;
      this.motivosCategoriaModal = [];
      this.setoresCategoriaModal = [];
      this.setorIdParaAdicionar = null;
      this.novoMotivoDescricao = "";
      this.novaCategoriaDescricao = "";
      this.novoMotivoNovaCategoria = "";
      this.editandoCategoriaId = null;
      this.descricaoCategoriaEditando = "";
      this.carregarSetoresTodos();
    },
    fecharModalCategoriasMotivos() {
      this.modalCategoriasMotivos = false;
    },
    abrirEditarCategoria() {
      if (!this.categoriaSelecionadaId) return;
      const c = this.categorias.find((x) => x.id === this.categoriaSelecionadaId);
      if (c) {
        this.editandoCategoriaId = c.id;
        this.descricaoCategoriaEditando = c.descricao || "";
      }
    },
    cancelarEditarCategoria() {
      this.editandoCategoriaId = null;
      this.descricaoCategoriaEditando = "";
    },
    async salvarEdicaoCategoria() {
      if (!this.editandoCategoriaId || !this.descricaoCategoriaEditando.trim()) return;
      this.salvandoEdicaoCategoria = true;
      try {
        await InconformidadeDataSource.updateCategoria(this.editandoCategoriaId, {
          descricao: this.descricaoCategoriaEditando.trim(),
        });
        await this.carregarCategorias();
        await this.carregarMotivos();
        const id = this.editandoCategoriaId;
        this.cancelarEditarCategoria();
        this.categoriaSelecionadaId = id;
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao atualizar categoria.";
      } finally {
        this.salvandoEdicaoCategoria = false;
      }
    },
    abrirExcluirCategoria() {
      if (!this.categoriaSelecionadaId) return;
      const c = this.categorias.find((x) => x.id === this.categoriaSelecionadaId);
      if (c) {
        this.categoriaEmExclusao = c;
        this.modalExcluirCategoria = true;
      }
    },
    fecharExcluirCategoria() {
      if (!this.excluindoCategoria) {
        this.modalExcluirCategoria = false;
        this.categoriaEmExclusao = null;
      }
    },
    async confirmarExcluirCategoria() {
      if (!this.categoriaEmExclusao) return;
      this.excluindoCategoria = true;
      try {
        await InconformidadeDataSource.deleteCategoria(this.categoriaEmExclusao.id);
        this.modalExcluirCategoria = false;
        const idRemovido = this.categoriaEmExclusao.id;
        this.categoriaEmExclusao = null;
        if (this.categoriaSelecionadaId === idRemovido) {
          this.categoriaSelecionadaId = null;
          this.editandoCategoriaId = null;
          this.descricaoCategoriaEditando = "";
        }
        await this.carregarCategorias();
        await this.carregarMotivos();
      } catch (err) {
        const msg = err.response?.data?.error || err.response?.data?.message || err.message;
        this.notificationMessage = msg || "Erro ao excluir categoria.";
      } finally {
        this.excluindoCategoria = false;
      }
    },
    async criarMotivoNaCategoriaExistente() {
      if (!this.categoriaSelecionadaId || !this.novoMotivoDescricao.trim()) return;
      this.salvandoMotivo = true;
      try {
        await InconformidadeDataSource.createMotivo({
          categoria_id: this.categoriaSelecionadaId,
          descricao: this.novoMotivoDescricao.trim(),
        });
        this.novoMotivoDescricao = "";
        await this.carregarMotivos();
        await this.carregarMotivosCategoriaModal();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao criar motivo.";
      } finally {
        this.salvandoMotivo = false;
      }
    },
    abrirExcluirMotivo(motivo) {
      this.motivoEmExclusao = motivo;
      this.modalExcluirMotivo = true;
    },
    fecharExcluirMotivo() {
      if (!this.excluindoMotivo) {
        this.modalExcluirMotivo = false;
        this.motivoEmExclusao = null;
        this.excluindoMotivoId = null;
      }
    },
    async confirmarExcluirMotivo() {
      if (!this.motivoEmExclusao) return;
      this.excluindoMotivo = true;
      this.excluindoMotivoId = this.motivoEmExclusao.id;
      try {
        await InconformidadeDataSource.deleteMotivo(this.motivoEmExclusao.id);
        this.modalExcluirMotivo = false;
        this.motivoEmExclusao = null;
        await this.carregarMotivos();
        await this.carregarMotivosCategoriaModal();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao excluir motivo.";
      } finally {
        this.excluindoMotivo = false;
        this.excluindoMotivoId = null;
      }
    },
    async criarCategoriaEMotivo() {
      if (!this.novaCategoriaDescricao.trim() || !this.novoMotivoNovaCategoria.trim()) return;
      this.salvandoCategoria = true;
      try {
        const categoria = await InconformidadeDataSource.createCategoria({
          descricao: this.novaCategoriaDescricao.trim(),
        });
        await InconformidadeDataSource.createMotivo({
          categoria_id: categoria.id,
          descricao: this.novoMotivoNovaCategoria.trim(),
        });
        this.novaCategoriaDescricao = "";
        this.novoMotivoNovaCategoria = "";
        await this.carregarCategorias();
        await this.carregarMotivos();
      } catch (err) {
        this.notificationMessage = err.response?.data?.message || err.message || "Erro ao criar categoria e motivo.";
      } finally {
        this.salvandoCategoria = false;
      }
    },
  },
};
</script>

<style scoped>
/* Evita espaço em branco à direita: o wrapper da modal encolhe ao conteúdo */
:deep(.modal-categorias-motivos) {
  width: fit-content;
  max-width: calc(100vw - 2rem);
}

.modal-cat-motivos {
  min-width: 420px;
  /* max-width: 520px; */
  padding: 0.25rem;
  box-sizing: border-box;
}

.modal-cat-motivos__titulo {
  margin: 0 0 0.25rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: var(--cor-texto, #1a1a1a);
}

.modal-cat-motivos__subtitulo {
  margin: 0 0 1.25rem;
  font-size: 0.875rem;
  color: var(--cor-texto-secundario, #666);
  line-height: 1.35;
}

.modal-cat-motivos__chips {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.25rem;
}

.modal-cat-motivos__chip {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  border: 1px solid var(--cor-separador, #ddd);
  border-radius: 8px;
  background: var(--cor-fundo, #fff);
  color: var(--cor-texto, #1a1a1a);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.modal-cat-motivos__chip:hover {
  border-color: var(--cor-primaria, #2563eb);
  background: rgba(37, 99, 235, 0.06);
  color: var(--cor-primaria, #2563eb);
}

.modal-cat-motivos__chip--ativo {
  border-color: var(--cor-primaria, #2563eb);
  background: var(--cor-primaria, #2563eb);
  color: #fff;
}

.modal-cat-motivos__chip--ativo:hover {
  background: var(--cor-primaria, #2563eb);
  color: #fff;
  opacity: 0.95;
}

.modal-cat-motivos__painel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--cor-separador, #e5e7eb);
  border-radius: 10px;
  background: var(--cor-fundo-secundario, #f9fafb);
}

.modal-cat-motivos__campo {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.modal-cat-motivos__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--cor-texto-secundario, #555);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.modal-cat-motivos__input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  font-size: 0.95rem;
  border: 1px solid var(--cor-separador, #ddd);
  border-radius: 6px;
  background: #fff;
  color: var(--cor-texto, #1a1a1a);
  box-sizing: border-box;
}

.modal-cat-motivos__input:focus {
  outline: none;
  border-color: var(--cor-primaria, #2563eb);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
}

.modal-cat-motivos__lista {
  max-height: 180px;
  overflow-y: auto;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--cor-separador, #e5e7eb);
  border-radius: 6px;
  background: #fff;
}

.modal-cat-motivos__lista--grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 200px));
  gap: 0.5rem;
}

.modal-cat-motivos__lista--grid .modal-cat-motivos__item {
  border-bottom: none;
  padding: 0.5rem 0.6rem;
  border: 1px solid var(--cor-separador, #e5e7eb);
  border-radius: 8px;
  background: var(--cor-fundo, #fff);
  box-sizing: border-box;
  min-width: 0;
}

.modal-cat-motivos__item {
  padding: 0.35rem 0;
  font-size: 0.9rem;
  color: var(--cor-texto, #1a1a1a);
  border-bottom: 1px solid var(--cor-separador, #f0f0f0);
  line-height: 1.4;
}

.modal-cat-motivos__item:last-child {
  border-bottom: none;
}

.modal-cat-motivos__item--com-acao {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.modal-cat-motivos__item-texto {
  flex: 1;
  min-width: 0;
}

.modal-cat-motivos__item-btn-remover {
  flex-shrink: 0;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid var(--cor-erro, #dc2626);
  border-radius: 4px;
  background: transparent;
  color: var(--cor-erro, #dc2626);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.modal-cat-motivos__item-btn-remover:hover:not(:disabled) {
  background: var(--cor-erro, #dc2626);
  color: #fff;
}

.modal-cat-motivos__item-btn-remover:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-cat-motivos__vazio {
  margin: 0;
  font-size: 0.875rem;
  color: var(--cor-texto-secundario, #888);
  font-style: italic;
}

.modal-cat-motivos__acoes {
  display: flex;
  justify-content: flex-end;
  padding-top: 0.25rem;
}

.modal-cat-motivos__btn {
  padding: 0.6rem 1.25rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.05s;
}

.modal-cat-motivos__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-cat-motivos__btn--primario {
  background: var(--cor-primaria, #2563eb);
  color: #fff;
}

.modal-cat-motivos__btn--primario:not(:disabled):hover {
  opacity: 0.9;
}

.modal-cat-motivos__btn--primario:not(:disabled):active {
  transform: scale(0.98);
}

.modal-cat-motivos__btn--secundario {
  background: transparent;
  color: var(--cor-primaria, #2563eb);
  border: 1px solid var(--cor-primaria, #2563eb);
}

.modal-cat-motivos__btn--secundario:not(:disabled):hover {
  background: rgba(37, 99, 235, 0.08);
}
</style>
